import { Badge, Box, Link } from '@ksyos/design-system';
import { useSearchParams } from '@ksyos/router';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { styled } from 'styled-components';
import {
    digidFaqUrl,
    digidUrl,
    servicedeskPhoneNumber,
    serviceDeskPhoneNumberDisplay,
} from '../../../common/constants';
import { PatientDataError } from '../../../common/enums/PatientDataError';
import { SamlErrorType } from '../../../common/enums/SamlErrorType';
import { HeroText } from '../elements/atoms/heroText/HeroText';
import * as styles from '../elements/atoms/link/Link.module.scss';
import { Paragraph } from '../elements/atoms/paragraph/Paragraph';
import { Alert } from '../elements/molecules/alert/Alert';
import { Article } from '../elements/molecules/article/article';
import { Container } from '../elements/molecules/container/Container';
import { Hero } from '../elements/molecules/hero/Hero';
import { Main } from '../elements/molecules/main/Main';
import { PageSection } from '../elements/molecules/pageSection/PageSection';
import { Footer } from '../elements/organisms/footer/Footer';
import { Header } from '../elements/organisms/header/Header';
import { LoginForm } from '../elements/organisms/loginForm/LoginForm';
import { PageWrapper } from '../elements/organisms/pageWrapper/PageWrapper';

export function Login() {
    const [searchParams] = useSearchParams();
    const error = searchParams.get('error');
    const autoSend = searchParams.get('autoSend');
    const relayState = searchParams.getOptional('relayState');

    return (
        <>
            {!autoSend && (
                <PageWrapper>
                    <Header />
                    <Main>
                        <Container isFluidOnMobile>
                            <StyledHero
                                error={!!error}
                                imgUrlDesktop="/assets/images/thuisconsult-large.svg"
                                imgUrlMobile="/assets/images/thuisconsult-small.svg"
                                isBold
                                subtitle="Welkom bij MijnKsyos"
                            >
                                <StyledLoginFormContainer>
                                    <Paragraph className="mb-4">
                                        Bekijk de volgende stap voor jouw zorg in MijnKsyos.
                                    </Paragraph>

                                    {error === SamlErrorType.UnknownBsn && (
                                        <Alert
                                            alertHeading="Jouw gegevens zijn niet bekend in ons systeem."
                                            variant="warning"
                                        >
                                            <Paragraph>Klopt dit niet? Bel dan met de Ksyos Servicedesk:</Paragraph>
                                            <a
                                                aria-label="Bel Ksyos servicedesk"
                                                href={`tel:${servicedeskPhoneNumber}`}
                                            >
                                                {serviceDeskPhoneNumberDisplay}
                                            </a>
                                        </Alert>
                                    )}

                                    {error === SamlErrorType.AccessDenied && (
                                        <Alert
                                            alertHeading="Het inloggen met DigiD is geannuleerd"
                                            variant="warning"
                                        >
                                            <Paragraph>
                                                Log in met je DigiD, of raadpleeg de{' '}
                                                <a
                                                    className={styles.link}
                                                    href={digidFaqUrl}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    veelgestelde vragen
                                                </a>{' '}
                                                voor hulp.
                                            </Paragraph>
                                        </Alert>
                                    )}

                                    {error === SamlErrorType.FlowError && (
                                        <Alert variant="warning">
                                            <Paragraph>
                                                Er is een fout opgetreden in de communicatie met DigiD. Probeer het
                                                later nogmaals. Indien deze fout blijft aanhouden, kijk dan op de
                                                website <a href={digidUrl}>digidUrl</a> voor de laatste informatie.
                                            </Paragraph>
                                        </Alert>
                                    )}

                                    {error === PatientDataError.procedureSent && (
                                        <Alert
                                            alertHeading="Je antwoorden zijn al verstuurd. Je kunt de vragenlijst niet nogmaals invullen."
                                            variant="warning"
                                        >
                                            <Paragraph>
                                                Log in met DigiD om de huisarts een vraag te stellen. Zo hoef je niet
                                                naar de praktijk te komen.
                                            </Paragraph>
                                        </Alert>
                                    )}

                                    {error === PatientDataError.invalidUrl && (
                                        <Alert variant="warning">
                                            <Paragraph>De link is niet geldig.</Paragraph>
                                        </Alert>
                                    )}

                                    <LoginForm
                                        relayState={relayState}
                                        submitButtonText="Log in met DigiD"
                                    ></LoginForm>
                                    <Paragraph>
                                        <Link
                                            href={digidUrl}
                                            variant="externalLink"
                                        >
                                            Heb je geen DigiD?
                                        </Link>
                                    </Paragraph>
                                </StyledLoginFormContainer>
                            </StyledHero>

                            <Row>
                                <Col>
                                    <PageSection bgColor="white">
                                        <Article>
                                            <HeroText>Over Ksyos</HeroText>
                                            <Paragraph>
                                                Met 4.500 medisch specialisten en paramedici en 7.500 huisartsen is
                                                Ksyos de grootste zorginstelling van Nederland. Ksyos maakt de zorg
                                                slimmer, beter en menselijker. Waar je ook bent, makkelijk online.
                                            </Paragraph>
                                        </Article>
                                    </PageSection>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <PageSection bgColor="white">
                                        <Article>
                                            <HeroText>Onze medische specialismen</HeroText>
                                            <div className="badge-container">
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="pulmonology"
                                                        color="blue"
                                                        size="large"
                                                        text="Long"
                                                    />
                                                </div>
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="mentalHealth"
                                                        color="orange"
                                                        size="large"
                                                        text="GGZ"
                                                    />
                                                </div>
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="dermatology"
                                                        color="magenta"
                                                        size="large"
                                                        text="Huid"
                                                    />
                                                </div>
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="somnology"
                                                        color="green"
                                                        size="large"
                                                        text="Slaap"
                                                    />
                                                </div>
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="ophthalmology"
                                                        color="cyan"
                                                        size="large"
                                                        text="Oog"
                                                    />
                                                </div>
                                                <div className="badge-item">
                                                    <Badge
                                                        carePathIcon="cardiology"
                                                        color="yellow"
                                                        size="large"
                                                        text="Hart"
                                                    />
                                                </div>
                                            </div>
                                        </Article>
                                    </PageSection>
                                </Col>
                            </Row>
                        </Container>
                    </Main>
                    <Footer />
                </PageWrapper>
            )}
            {autoSend && (
                <LoginForm
                    autoSend={true}
                    relayState={relayState}
                    submitButtonText="Log in met DigiD"
                ></LoginForm>
            )}
        </>
    );
}

const StyledHero = styled(Hero)`
    @media screen and (max-width: 455px) {
        min-height: 580px;
    }

    @media screen and (min-width: 768px) {
        justify-content: center;
    }
`;

const StyledLoginFormContainer = styled(Box)`
    @media screen and (min-width: 576px) {
        width: 100%;
    }

    @media screen and (min-width: 768px) {
        width: 50%;
    }
`;
